import { useLayoutEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import "./socialmediaanalysisview.css";

// Theme colors: [Recommendation, Asset Confidence, Brand Choice, Burden of Disease]
export const baseColors = ["#80e4cf", "#7949f0", "#630163", "#b502b1"];

function DFOAnalysisChart() {
  useLayoutEffect(() => {
    const root = am5.Root.new("dfoAnalysisChartId");
    if (root._logo) root._logo.dispose();
    root.setThemes([am5themes_Animated.new(root)]);

    // Chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 24
      })
    );

    // Title (optional)
    chart.children.unshift(
      am5.Label.new(root, {
        text: "",
        fontSize: 18,
        fontWeight: "600",
        x: am5.p50,
        centerX: am5.p50,
        paddingTop: 2,
        paddingBottom: 2
      })
    );

    // Raw counts (unchanged)
    const rows = [
      { phase: "Pre Congress",    theme1: 39, theme2: 16, theme3: 25, theme4: 21 },
      { phase: "During Congress", theme1: 16, theme2: 40, theme3: 14, theme4: 30 },
      { phase: "Post Congress",   theme1: 30, theme2: 38, theme3: 38, theme4: 35 }
    ];

    // Build % labels (bars use counts; labels use %)
    const pct = (v, t) => (t ? (v / t) * 100 : 0);
    const fmtPct = (v) => `${Math.round(v)}%`;
    const data = rows.map(r => {
      const total = r.theme1 + r.theme2 + r.theme3 + r.theme4;
      return {
        ...r,
        total,
        theme1PctLabel: fmtPct(pct(r.theme1, total)),
        theme2PctLabel: fmtPct(pct(r.theme2, total)),
        theme3PctLabel: fmtPct(pct(r.theme3, total)),
        theme4PctLabel: fmtPct(pct(r.theme4, total))
      };
    });

    // Y axis = phases (center bars in their row)
    const yRenderer = am5xy.AxisRendererY.new(root, {
      cellStartLocation: 0.2,
      cellEndLocation: 0.8
    });
    yRenderer.grid.template.setAll({ location: 1, strokeOpacity: 0.1 });

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "phase",
        renderer: yRenderer
      })
    );
    yAxis.data.setAll(data);

    // X axis = numeric counts (NOT percentage)
    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererX.new(root, { strokeOpacity: 0.1, minGridDistance: 40 })
      })
    );
    xAxis.set("numberFormatter", am5.NumberFormatter.new(root, { numberFormat: "#,###" }));

    // Legend
    const legend = chart.children.push(
      am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 8 })
    );

    // Series factory: bars use counts; bullets show % labels
    function makeSeries(name, valueField, pctLabelField, colorHex) {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name,
          stacked: true,
          xAxis,
          yAxis,
          baseAxis: yAxis,
          valueXField: valueField,     // raw count -> numeric axis
          categoryYField: "phase"
        })
      );

      series.columns.template.setAll({
        height: am5.percent(100),       // centered thickness
        strokeOpacity: 0,
        fill: am5.color(colorHex),
        tooltipText: "{name} — {categoryY}: {valueX}"
      });

      // % label inside each segment
      series.bullets.push(() =>
        am5.Bullet.new(root, {
          locationX: 0.5,
          sprite: am5.Label.new(root, {
            text: `{${pctLabelField}}`,
            populateText: true,
            centerY: am5.p50,
            centerX: am5.p50,
            fontSize: 11,
            fontWeight: "600",
            fill: root.interfaceColors.get("alternativeText")
          })
        })
      );

      series.data.setAll(data);
      legend.data.push(series);
      series.appear(300);
      return series;
    }

    // Map to your new theme names
    makeSeries("Recommendation",    "theme1", "theme1PctLabel", baseColors[0]);
    makeSeries("Asset Confidence",  "theme2", "theme2PctLabel", baseColors[1]);
    makeSeries("Brand Choice",      "theme3", "theme3PctLabel", baseColors[2]);
    makeSeries("Burden of Disease", "theme4", "theme4PctLabel", baseColors[3]);

    // Totals at the right end of each stacked bar
    const totalSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "TotalsHelper",
        xAxis,
        yAxis,
        baseAxis: yAxis,
        valueXField: "total",
        categoryYField: "phase",
        stacked: false
      })
    );
    totalSeries.columns.template.setAll({ fillOpacity: 0, strokeOpacity: 0, width: 0, height: am5.percent(60) });
    totalSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationX: 1,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: "{total}",
          populateText: true,
          centerX: am5.p100,
          centerY: am5.p50,
          dx: 8,
          fontWeight: "700",
          fill: am5.color(0x333333),
          background: am5.RoundedRectangle.new(root, {
            fill: am5.color(0xffffff),
            fillOpacity: 0.9,
            cornerRadiusBL: 3, cornerRadiusBR: 3, cornerRadiusTL: 3, cornerRadiusTR: 3,
            stroke: am5.color(0xcccccc), strokeOpacity: 0.7
          }),
          paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2
        })
      })
    );
    totalSeries.data.setAll(data);
    totalSeries.appear(300);

    chart.appear(800, 100);
    return () => { root.dispose(); };
  }, []);

  return (
    <>
      <p className="chart-sub-text">Shifts in client key information topics across pre congress, live congress, and post congress phases.</p>
      <div id="dfoAnalysisChartId" className="dfoAnalysis"></div>
    </>
  );
}

export default DFOAnalysisChart;